import React, { useEffect, useState } from 'react';
import {
  Box, Button, Grid, IconButton, MenuItem, Paper, TextField, Typography,
  FormControl, InputLabel, Select, Switch, FormControlLabel, Card,
  CardContent, Avatar
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Person as PersonIcon } from '@mui/icons-material';
import { EquipmentItem, RequirementItem } from './index';
import { serviceManagementService } from '../../services/serviceManagementService';
import { topCleanersService } from '../../services/topCleanersService';

interface TopCleaner {
  top_cleaner_id?: number;
  service_id?: number;
  sp_id: number;
  icon?: string;
  cleaner_text: string;
  display_order: number;
  provider_name?: string;
  provider_photo?: string;
  _tempIconFile?: File | null; // temporary file reference for new items
}

interface AddonServiceItem {
  id: number;
  addon_service_id: number;
  is_included: boolean;
}

interface AddonServiceOption {
  addon_id: number;
  name: string;
  base_price: number;
}

interface RequirementOption {
  requirement_id: number;
  name?: string;
  description?: string;
}

interface ServiceRequirementsFormProps {
  equipment: EquipmentItem[];
  requirements: RequirementItem[];
  addonServices: AddonServiceItem[];
  onEquipmentChange: (equipment: EquipmentItem[]) => void;
  onRequirementsChange: (requirements: RequirementItem[]) => void;
  onAddonServicesChange: (addonServices: AddonServiceItem[]) => void;
  onTopCleanersChange: (topCleaners: TopCleaner[]) => void;
  onDeletedTopCleanerIdsChange: (ids: number[]) => void;
  serviceId?: number;
  serviceTypeId?: number;
  topCleaners?: TopCleaner[]; // initial list (optional) from parent
}

const ServiceRequirementsForm: React.FC<ServiceRequirementsFormProps> = (props) => {
  const {
    equipment = [],
    requirements = [],
    addonServices = [],
    onEquipmentChange = () => { },
    onRequirementsChange = () => { },
    onAddonServicesChange = () => { },
    onTopCleanersChange = () => { },
    onDeletedTopCleanerIdsChange = () => { },
    serviceId,
    topCleaners: initialTopCleaners = []
  } = props;

  // lists from API
  const [availableAddonServices, setAvailableAddonServices] = useState<AddonServiceOption[]>([]);
  const [availableRequirements, setAvailableRequirements] = useState<RequirementOption[]>([]);

  // top cleaners state
  const [topCleanerProviders, setTopCleanerProviders] = useState<any[]>([]);
  const [topCleaners, setTopCleaners] = useState<TopCleaner[]>(initialTopCleaners ?? []);
  const [selectedStaffId, setSelectedStaffId] = useState<number | ''>('');
  const [cleanerText, setCleanerText] = useState('');
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [deletedTopCleanerIds, setDeletedTopCleanerIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // keep parent updated
  useEffect(() => {
    onTopCleanersChange(topCleaners);
  }, [topCleaners, onTopCleanersChange]);

  useEffect(() => {
    onDeletedTopCleanerIdsChange(deletedTopCleanerIds);
  }, [deletedTopCleanerIds, onDeletedTopCleanerIdsChange]);

  // Normalize helpers
  const normalizeProviders = (providersRaw: any[]): { sp_id: number; name?: string; photo?: string }[] => {
    if (!Array.isArray(providersRaw)) return [];
    return providersRaw.map(p => ({
      sp_id: Number(p.sp_id ?? p.id ?? p.provider_id ?? 0),
      name: (p.sp_name ?? p.name ?? p.provider_name ?? '') as string,
      photo: p.photo ?? p.provider_photo ?? p.avatar ?? undefined
    })).filter(p => Number.isFinite(p.sp_id) && p.sp_id > 0);
  };

  const normalizeExistingCleaners = (cleanersRaw: any[]): TopCleaner[] => {
    if (!Array.isArray(cleanersRaw)) return [];
    return cleanersRaw.map((c: any, idx: number) => ({
      top_cleaner_id: c.top_cleaner_id ? Number(c.top_cleaner_id) : undefined,
      service_id: c.service_id ? Number(c.service_id) : serviceId,
      sp_id: Number(c.sp_id ?? c.provider_id ?? c.sp ?? 0),
      icon: c.icon ?? c.image ?? undefined,
      cleaner_text: c.cleaner_text ?? c.text ?? '',
      display_order: Number(c.display_order ?? c.order ?? (idx + 1)),
      provider_name: c.provider_name ?? c.sp_name ?? c.name ?? undefined,
      provider_photo: c.provider_photo ?? c.photo ?? undefined
    })).filter(c => Number.isFinite(c.sp_id) && c.sp_id > 0);
  };

  // Single consolidated data loader — run on mount and when serviceId changes
  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      setIsLoading(true);

      try {
        // Requirements
        try {
          const reqs = await serviceManagementService.getRequirementsList();
          const normalizedReqs: RequirementOption[] = Array.isArray(reqs)
            ? reqs.map((r: any) => ({
              requirement_id: Number(r.requirement_id ?? r.id ?? r.req_id ?? 0),
              name: r.name ?? r.title ?? undefined,
              description: r.description ?? undefined
            })).filter(r => Number.isFinite(r.requirement_id) && r.requirement_id > 0)
            : [];
          if (!cancelled) setAvailableRequirements(normalizedReqs);
        } catch (err) {
          if (!cancelled) setAvailableRequirements([]);
          // keep quiet; dev console will show service errors
          // console.error('Failed to load requirements:', err);
        }

        // Addon services
        try {
          const addonsRaw = await serviceManagementService.getAddonServicesList(serviceId);
          const normalizedAddons: AddonServiceOption[] = Array.isArray(addonsRaw)
            ? addonsRaw.map((a: any) => ({
              addon_id: Number(a.addon_id ?? a.id ?? 0),
              name: a.name ?? a.title ?? `Addon ${a.addon_id ?? a.id ?? ''}`,
              base_price: Number(a.base_price ?? a.price ?? 0)
            })).filter(a => Number.isFinite(a.addon_id) && a.addon_id > 0)
            : [];
          if (!cancelled) setAvailableAddonServices(normalizedAddons);
        } catch (err) {
          if (!cancelled) setAvailableAddonServices([]);
          // console.error('Failed to load addon services:', err);
        }

        // Top cleaner providers
        try {
          const provRaw = await topCleanersService.getAvailableProviders();
          const provs = normalizeProviders(Array.isArray(provRaw) ? provRaw : (provRaw?.staff ?? provRaw?.data ?? []));
          if (!cancelled) setTopCleanerProviders(provs);
        } catch (err) {
          if (!cancelled) setTopCleanerProviders([]);
          // console.error('Failed to load top cleaner providers:', err);
        }

        // Existing top cleaners for this service (if serviceId provided and parent didn't pass initial list)
        if (serviceId) {
          try {
            const existingRaw = await topCleanersService.getServiceTopCleaners(serviceId);
            const existing = normalizeExistingCleaners(Array.isArray(existingRaw) ? existingRaw : (existingRaw?.data ?? existingRaw));
            if (!cancelled) setTopCleaners(existing);
            if (existing.length > 0) {
              setSelectedStaffId(existing[0].sp_id);
            }
          } catch (err) {
            // console.error('Failed to load existing top cleaners:', err);
          }
        }

      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadData();

    return () => { cancelled = true; };
    // NOTE: we deliberately do NOT depend on initialTopCleaners (it caused repeat calls).
    // Effect runs when serviceId changes.
  }, [serviceId]);

  // Top cleaners handlers
  const handleAddTopCleaner = () => {
    if (!selectedStaffId || !cleanerText) return;
    
    const provider = topCleanerProviders.find(p => Number(p.sp_id) === Number(selectedStaffId));
    const displayOrder = (topCleaners?.length ?? 0) + 1;

    // Create temporary cleaner object (not saved to DB yet)
    const newCleaner: TopCleaner = {
      top_cleaner_id: undefined, // undefined indicates it's not saved yet
      service_id: serviceId,
      sp_id: Number(selectedStaffId),
      icon: iconFile ? URL.createObjectURL(iconFile) : '',
      cleaner_text: cleanerText,
      display_order: displayOrder,
      provider_name: provider?.name,
      provider_photo: provider?.photo,
      _tempIconFile: iconFile // store file reference for later upload
    };

    setTopCleaners(prev => [...prev, newCleaner]);
    setCleanerText('');
    setIconFile(null);
  };

  const handleDeleteTopCleaner = async (id?: number | string) => {
    if (!id) return;
    try {
      setIsLoading(true);
      
      // Check if it's a temporary item (no top_cleaner_id)
      if (typeof id === 'string' && id.startsWith('temp-')) {
        // Just remove from local state
        setTopCleaners(prev => {
          const updated = prev.filter(c => 
            (c.top_cleaner_id ? c.top_cleaner_id.toString() : `temp-${c.sp_id}`) !== id
          );
          return updated.map((c, idx) => ({ ...c, display_order: idx + 1 }));
        });
      } else {
        // It's a saved item, delete from API
        try {
          await topCleanersService.deleteTopCleaner(id as number);
        } catch (err) {
          // if delete fails, continue to remove locally
        }
        setTopCleaners(prev => {
          const updated = prev.filter(c => c.top_cleaner_id !== id);
          return updated.map((c, idx) => ({ ...c, display_order: idx + 1 }));
        });
        if (typeof id === 'number' && id > 0) {
          setDeletedTopCleanerIds(prev => [...prev, id]);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateCleanerText = async (id?: number | string, text?: string) => {
    if (!id || text === undefined) return;
    
    // For temporary items, just update local state
    if (typeof id === 'string' && id.startsWith('temp-')) {
      setTopCleaners(prev => prev.map(c => {
        const cleanerId = c.top_cleaner_id ? c.top_cleaner_id.toString() : `temp-${c.sp_id}-${c.display_order}`;
        return cleanerId === id ? { ...c, cleaner_text: text } : c;
      }));
      return;
    }
    
    // For saved items, update via API
    try {
      setIsLoading(true);
      const fd = new FormData();
      fd.append('cleaner_text', text);
      await topCleanersService.updateTopCleaner(id as number, fd);
      setTopCleaners(prev => prev.map(c => c.top_cleaner_id === id ? { ...c, cleaner_text: text } : c));
    } catch (err) {
      // console.error('Error updating cleaner text:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateDisplayOrder = async (id?: number | string, order?: number) => {
    if (!id || order === undefined || Number.isNaN(order)) return;
    
    // For temporary items, just update local state
    if (typeof id === 'string' && id.startsWith('temp-')) {
      setTopCleaners(prev => prev.map(c => {
        const cleanerId = c.top_cleaner_id ? c.top_cleaner_id.toString() : `temp-${c.sp_id}-${c.display_order}`;
        return cleanerId === id ? { ...c, display_order: order } : c;
      }));
      return;
    }
    
    // For saved items, update via API
    try {
      setIsLoading(true);
      const fd = new FormData();
      fd.append('display_order', String(order));
      await topCleanersService.updateTopCleaner(id as number, fd);
      setTopCleaners(prev => prev.map(c => c.top_cleaner_id === id ? { ...c, display_order: order } : c));
    } catch (err) {
      // console.error('Error updating display order:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Equipment functions
  const updateEquipment = (id: number, field: keyof EquipmentItem, value: any) => {
    const parsed = field === 'quantity' || field === 'equipment_id' ? Number(value) : value;
    onEquipmentChange(equipment.map(e => e.id === id ? { ...e, [field]: parsed } : e));
  };

  const addEquipment = () => {
    const nextId = equipment.length > 0 ? Math.max(...equipment.map(e => e.id)) + 1 : 1;
    onEquipmentChange([...equipment, { id: nextId, equipment_id: 0, quantity: 1 } as EquipmentItem]);
  };

  const removeEquipment = (id: number) => {
    onEquipmentChange(equipment.filter(e => e.id !== id));
  };

  // Addon functions
  const addAddonService = () => {
    if (availableAddonServices.length === 0) return;
    const nextId = addonServices.length > 0 ? Math.max(...addonServices.map(a => a.id)) + 1 : 1;
    const availableAddon = availableAddonServices.find(addon => !addonServices.some(a => Number(a.addon_service_id) === addon.addon_id));
    if (!availableAddon) return;
    const newAddon: AddonServiceItem = { id: nextId, addon_service_id: availableAddon.addon_id, is_included: true };
    onAddonServicesChange([...addonServices, newAddon]);
  };

  const removeAddonService = (id: number) => {
    onAddonServicesChange(addonServices.filter(a => a.id !== id));
  };

  const updateAddonService = (id: number, addon_service_id: string | number) => {
    const value = Number(addon_service_id);
    if (isNaN(value)) return;
    onAddonServicesChange(addonServices.map(a => a.id === id ? { ...a, addon_service_id: value } : a));
  };

  // Requirement functions
  const addRequirement = () => {
    if (availableRequirements.length === 0) return;
    const nextId = requirements.length > 0 ? Math.max(...requirements.map(r => r.id)) + 1 : 1;
    const availableReq = availableRequirements.find(req => !requirements.some(r => Number(r.requirement_id) === req.requirement_id));
    if (!availableReq) return;
    onRequirementsChange([...requirements, { id: nextId, requirement_id: availableReq.requirement_id, is_mandatory: false } as RequirementItem]);
  };

  const removeRequirement = (id: number) => {
    onRequirementsChange(requirements.filter(r => r.id !== id));
  };

  const updateRequirementId = (id: number, requirementId: number) => {
    onRequirementsChange(requirements.map(r => r.id === id ? { ...r, requirement_id: requirementId } : r));
  };

  const updateRequirementMandatory = (id: number, is_mandatory: boolean) => {
    onRequirementsChange(requirements.map(r => r.id === id ? { ...r, is_mandatory } : r));
  };

  // Render Top Cleaners Section
  const renderTopCleanersSection = () => {
  const isEditMode = !!serviceId; // true if editing service
  const selectedProvider =
  topCleanerProviders.find(p => p.sp_id === selectedStaffId) || null;


  return (
    <Box sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        Top Cleaners (Recommended Staff)
      </Typography>

      {/* ---------- STAFF SELECT (SHOW WHEN NO CLEANERS EXIST) ---------- */}
    {topCleaners.length === 0 && (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Select Staff
        </Typography>

        <FormControl fullWidth size="small">
          <InputLabel>Select Staff</InputLabel>
          <Select
            label="Select Staff"
            value={selectedStaffId}
            onChange={(e) => setSelectedStaffId(Number(e.target.value))}
          >
            {topCleanerProviders.map((p) => (
              <MenuItem key={p.sp_id} value={p.sp_id}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>
    )}


      {/* ---------- SHOW SELECTED STAFF ALWAYS ---------- */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Selected Staff
        </Typography>

        {selectedProvider ? (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Avatar src={selectedProvider.photo} sx={{ width: 40, height: 40, mr: 2 }} />
            <Typography>{selectedProvider.name}</Typography>
          </Box>
        ) : (
          <Typography color="text.secondary">No staff assigned</Typography>
        )}
      </Paper>

      {/* ---------- CLEANERS LIST ---------- */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Cleaner Details
        </Typography>

        {/* Existing cleaners */}
        {topCleaners.length > 0 &&
          topCleaners
            .slice()
            .sort((a, b) => a.display_order - b.display_order)
            .map((c, index) => {
              const rowId = c.top_cleaner_id || `temp-${index}`;

              return (
                <Box
                  key={rowId}
                  sx={{
                    p: 2,
                    border: "1px solid #ddd",
                    borderRadius: 2,
                    mb: 2,
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                      <TextField
                        label="Cleaner Text"
                        fullWidth
                        size="small"
                        value={c.cleaner_text}
                        onChange={(e) =>
                          handleUpdateCleanerText(rowId, e.target.value)
                        }
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Avatar src={c.icon} sx={{ width: 45, height: 45 }} />
                    </Grid>

                    <Grid item xs={6} md={2}>
                      <TextField
                        type="number"
                        size="small"
                        label="Order"
                        value={c.display_order}
                        onChange={(e) =>
                          handleUpdateDisplayOrder(rowId, Number(e.target.value))
                        }
                      />
                    </Grid>

                    <Grid item xs={6} md={2}>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteTopCleaner(rowId)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>
              );
            })}

        {/* Add new cleaner row */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Cleaner Text"
              value={cleanerText}
              onChange={(e) => setCleanerText(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Button variant="outlined" component="label" fullWidth>
              Upload Icon
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files?.length) {
                    setIconFile(e.target.files[0]);
                  }
                }}
              />
            </Button>
            {iconFile && (
              <Typography variant="caption">{iconFile.name}</Typography>
            )}
          </Grid>

          <Grid item xs={12} md={2}>
            <Button fullWidth variant="contained" onClick={handleAddTopCleaner}>
              Add
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};


  // MAIN RETURN
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Cleaning Equipment
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
        <Grid container spacing={2}>
          {equipment.map((e) => (
            <React.Fragment key={e.id}>
              <Grid item xs={12} md={7}>
                <TextField
                  fullWidth
                  select
                  label="Equipment"
                  value={e.equipment_id ?? ''}
                  onChange={(ev) => updateEquipment(e.id, 'equipment_id', ev.target.value)}
                >
                  <MenuItem value={1}>Pressure Washer</MenuItem>
                  <MenuItem value={2}>Foam Cannon</MenuItem>
                  <MenuItem value={3}>Microfiber Towels</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={8} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  inputProps={{ min: 1 }}
                  label="Quantity"
                  value={e.quantity ?? 1}
                  onChange={(ev) => updateEquipment(e.id, 'quantity', Number(ev.target.value))}
                />
              </Grid>

              <Grid item xs={4} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton color="error" onClick={() => removeEquipment(e.id)} aria-label="remove-equipment">
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </React.Fragment>
          ))}

          <Grid item xs={12}>
            <Button startIcon={<AddIcon />} onClick={addEquipment} variant="outlined">
              Add More Equipment
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="h6" gutterBottom>
        Customer Requirements (select existing)
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
        <Grid container spacing={2}>
          {requirements.map((r) => (
            <React.Fragment key={r.id}>
              <Grid item xs={8}>
                <FormControl fullWidth>
                  <InputLabel id={`req-label-${r.id}`}>Requirement</InputLabel>
                  <Select
                    labelId={`req-label-${r.id}`}
                    label="Requirement"
                    value={Number(r.requirement_id) ?? ''}
                    onChange={(ev) => updateRequirementId(r.id, Number(ev.target.value))}
                  >
                    {availableRequirements.map((item) => (
                      <MenuItem key={item.requirement_id} value={item.requirement_id}>
                        {item.name ?? item.description ?? `#${item.requirement_id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!r.is_mandatory}
                      onChange={(e) => updateRequirementMandatory(r.id, e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Mandatory"
                />
              </Grid>

              <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton color="error" onClick={() => removeRequirement(r.id)} aria-label="remove-requirement">
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </React.Fragment>
          ))}

          <Grid item xs={12}>
            <Button startIcon={<AddIcon />} onClick={addRequirement} variant="outlined">
              Add More Requirements
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        Available Addon Services
      </Typography>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {addonServices.length > 0 ? (
            addonServices.map((addon) => (
              <React.Fragment key={addon.id}>
                <Grid item xs={10} md={11}>
                  <FormControl fullWidth>
                    <InputLabel id={`addon-label-${addon.id}`}>Addon Service</InputLabel>
                    <Select
                      labelId={`addon-label-${addon.id}`}
                      label="Addon Service"
                      value={Number(addon.addon_service_id) ?? ''}
                      onChange={(e) => updateAddonService(addon.id, e.target.value)}
                    >
                      {availableAddonServices
                        .filter(item =>
                          !addonServices.some(a => Number(a.addon_service_id) === item.addon_id) ||
                          Number(addon.addon_service_id) === item.addon_id
                        )
                        .map(item => (
                          <MenuItem key={item.addon_id} value={item.addon_id}>
                            {item.name} (₹{item.base_price})
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={2} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton color="error" onClick={() => removeAddonService(addon.id)} aria-label="remove-addon">
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </React.Fragment>
            ))
          ) : (
            <Grid item xs={12}>
              <Typography color="textSecondary" align="center" sx={{ py: 2 }}>
                No addon services selected
              </Typography>
            </Grid>
          )}

          <Grid item xs={12}>
            <Button startIcon={<AddIcon />} onClick={addAddonService} variant="outlined">
              Add Addon Service
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Top Cleaners Section */}
      {renderTopCleanersSection()}
    </Box>
  );
};

export default ServiceRequirementsForm;
